/**
 * WorkbenchDrawer — the specialist's computer, docked at the bottom between rail and stream:
 * Files, Terminal (live tail), Browser (live view), Outputs (charts, PDFs, drafts).
 *
 * Height: drag handle, min 160 px, max 70 vh, 280 px by default when open, 36 px when closed; the
 * current height is published as --agent-drawer-h on <html> so the 3D stage sits above it.
 * wb.ui events arrive through `wb` (reducer state): tab, open_file, zoom, scroll and resize
 * (+/- 120 px, max = 70 vh, min = closed).
 */

import { useEffect, useRef, useState } from 'react';
import {
  FolderOpen, Terminal, Globe, LayoutGrid, ChevronUp, ChevronDown, GripHorizontal,
} from 'lucide-react';
import FilesView from './FilesView.jsx';
import TerminalView from './TerminalView.jsx';
import BrowserView from './BrowserView.jsx';
import OutputsView from './OutputsView.jsx';
import './WorkbenchDrawer.css';

const CLOSED_H = 36;
const MIN_H = 160;
const DEFAULT_H = 280;
const STEP_H = 120;

const TABS = [
  { key: 'files', label: 'Files', Icon: FolderOpen },
  { key: 'terminal', label: 'Terminal', Icon: Terminal },
  { key: 'browser', label: 'Browser', Icon: Globe },
  { key: 'outputs', label: 'Outputs', Icon: LayoutGrid },
];

const maxHeight = () => Math.round((typeof window !== 'undefined' ? window.innerHeight : 900) * 0.7);
const clampH = (h) => Math.max(MIN_H, Math.min(maxHeight(), Math.round(h)));

export default function WorkbenchDrawer({
  wb, files = [], terminal = [], browser = null, outputs = [], drafts = [], draftHighlight = {},
  caseId = null, fileUrl = null, editDraftId = null,
  onTab, onToggle, onResize, onOpenFile, onCloseFile, onDraftOp, onDraftFocus, onEditDraft,
}) {
  const open = Boolean(wb?.open);
  const tab = wb?.tab ?? 'outputs';
  const [height, setHeight] = useState(DEFAULT_H);
  const bodyRef = useRef(null);
  const dragRef = useRef(null);

  // Publish the drawer height for the stage layout; clear it when the drawer goes away.
  useEffect(() => {
    document.documentElement.style.setProperty('--agent-drawer-h', `${open ? height : CLOSED_H}px`);
  }, [open, height]);
  useEffect(() => () => document.documentElement.style.removeProperty('--agent-drawer-h'), []);

  // Voice / agent resize (+, -, max, min) and scroll. Each event key applies once; keys present at
  // mount are history (the drawer remounts when agent mode is re-entered) and are skipped.
  const heightRef = useRef(height);
  const onResizeRef = useRef(onResize);
  useEffect(() => { heightRef.current = height; onResizeRef.current = onResize; });

  const sizeKey = wb?.size ? `${wb.size.op}|${wb.size.n}` : null;
  const lastSizeRef = useRef(sizeKey);
  useEffect(() => {
    if (!sizeKey || sizeKey === lastSizeRef.current) return;
    lastSizeRef.current = sizeKey;
    const op = sizeKey.split('|')[0];
    const h = heightRef.current;
    let next = h;
    if (op === '+') next = clampH(h + STEP_H);
    else if (op === '-') next = clampH(h - STEP_H);
    else if (op === 'max') next = maxHeight();
    setHeight(next);
    onResizeRef.current?.(op === 'min' ? CLOSED_H : next);
  }, [sizeKey]);

  const scrollKey = wb?.scroll ? `${wb.scroll.direction}|${wb.scroll.n}` : null;
  const lastScrollRef = useRef(scrollKey);
  useEffect(() => {
    if (!scrollKey || scrollKey === lastScrollRef.current) return;
    lastScrollRef.current = scrollKey;
    const el = bodyRef.current?.querySelector('.wbd__scroller');
    if (!el) return;
    const dir = scrollKey.startsWith('up') ? -1 : 1;
    el.scrollBy({ top: dir * Math.max(80, el.clientHeight * 0.6), behavior: 'smooth' });
  }, [scrollKey]);

  const onPointerDown = (e) => {
    if (!open) return;
    e.preventDefault();
    dragRef.current = { y: e.clientY, h: height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setHeight(clampH(d.h + (d.y - e.clientY)));
  };
  const onPointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    onResize?.(height);
  };

  const fileUrlFor = fileUrl && caseId ? (path) => fileUrl(caseId, path) : null;
  const counts = {
    files: files.length,
    terminal: terminal.length,
    browser: browser?.actions?.length ?? 0,
    outputs: outputs.length + drafts.filter((d) => d.status !== 'superseded').length,
  };

  return (
    <section
      className={`wbd${open ? ' is-open' : ''}`}
      style={{ height: open ? height : CLOSED_H }}
      aria-label="Agent Workbench"
    >
      <div
        className="wbd__handle"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize the Workbench"
        tabIndex={open ? 0 : -1}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); const h = clampH(height + 40); setHeight(h); onResize?.(h); }
          if (e.key === 'ArrowDown') { e.preventDefault(); const h = clampH(height - 40); setHeight(h); onResize?.(h); }
        }}
      >
        {open && <GripHorizontal size={12} aria-hidden="true" />}
      </div>

      <div className="wbd__bar">
        <span className="wbd__name">Workbench</span>
        <div className="wbd__tabs" role="tablist" aria-label="Workbench">
          {TABS.map((t) => {
            const TabIcon = t.Icon;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={open && tab === t.key}
                className={`wbd__tab${open && tab === t.key ? ' is-on' : ''}`}
                onClick={() => { onTab?.(t.key); if (!open) onToggle?.(true); }}
              >
                <TabIcon size={12} aria-hidden="true" />
                {t.label}
                {counts[t.key] > 0 && <span className="wbd__count">{counts[t.key]}</span>}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="wbd__icon wbd__toggle"
          onClick={() => onToggle?.(!open)}
          aria-label={open ? 'Close the Workbench' : 'Open the Workbench'}
          aria-expanded={open}
        >
          {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronUp size={14} aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <div className="wbd__body" ref={bodyRef} role="tabpanel">
          {tab === 'files' && (
            <FilesView files={files} openPath={wb?.path && !wb.path.startsWith('outputs/drafts/') ? wb.path : null} fileUrl={fileUrlFor} onOpen={onOpenFile} />
          )}
          {tab === 'terminal' && <TerminalView execs={terminal} />}
          {tab === 'browser' && <BrowserView browser={browser} />}
          {tab === 'outputs' && (
            <OutputsView
              outputs={outputs}
              drafts={drafts}
              highlight={draftHighlight}
              fileUrl={fileUrlFor}
              openPath={wb?.path && wb.path.startsWith('outputs/') ? wb.path : null}
              range={wb?.range ?? 'all'}
              editDraftId={editDraftId}
              onOpen={onOpenFile}
              onClose={onCloseFile}
              onDraftOp={onDraftOp}
              onDraftFocus={onDraftFocus}
              onEditDraft={onEditDraft}
            />
          )}
        </div>
      )}
    </section>
  );
}
